/**
 * <custom-product-options> drives the generic product customization block:
 *  - shows/hides per-unit cards (e.g. "Keycap 1", "Keycap 2") based on the selected
 *    variant's `custom.repeat_count` metafield
 *  - sums the surcharge of the customer's selections (global options + the designs on the
 *    keycaps that are actually visible) and keeps the hidden "Upgrades" variant option in
 *    sync with that total, so price and cart line reflect the real cost natively
 *  - blocks add-to-cart until every visible required group has a value
 *
 * Pricing model: price-affecting choices don't change price directly. Instead a machine-
 * generated "Upgrades" variant option carries the surcharge as real, pre-priced variants
 * (created by the tier sync). This controller computes the surcharge total, finds the variant
 * matching (base option values + that total), and selects its Upgrades value so Dawn's native
 * variant pipeline updates the price. Products with no "Upgrades" option simply collect line
 * item properties with no price impact.
 *
 * Relies on Dawn's pub/sub (constants.js / pubsub.js / global.js) and on VariantSelects reading
 * the full DOM checked-state in its `selectedOptionValues` getter — so setting the Upgrades
 * input before Dawn's fetch is enough to drive the correct variant.
 */
if (!customElements.get('custom-product-options')) {
  customElements.define(
    'custom-product-options',
    class CustomProductOptions extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.hasUpgradesOption = this.dataset.hasUpgrades === 'true';
        this.baseOptionNames = (this.dataset.baseOptionNames || '')
          .split('||')
          .map((name) => name.trim())
          .filter(Boolean);
        this.variantData = this.parseVariantData();
        this.currentBaseKey = null;
        this.currentRepeatCount = 0;

        this.productForm = document.getElementById(`product-form-${this.sectionId}`);

        this.onGroupChange = this.onGroupChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onBaseOptionChangeCapture = this.onBaseOptionChangeCapture.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.addEventListener('change', this.onGroupChange);
        this.productForm?.addEventListener('submit', this.onSubmit, true);

        // Capture phase on document (a stable node): fires before VariantSelects' bubble-phase
        // listener reads the selected options, and survives the variant-selects DOM being
        // swapped out on every variant change. Lets us correct the Upgrades value first so the
        // resulting fetch already requests a valid, correctly-priced variant.
        document.addEventListener('change', this.onBaseOptionChangeCapture, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.syncToVariant(this.dataset.initialVariantId);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.productForm?.removeEventListener('submit', this.onSubmit, true);
        document.removeEventListener('change', this.onBaseOptionChangeCapture, true);
      }

      get variantSelects() {
        // Queried fresh each time: Dawn replaces this element on every variant change.
        return document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      }

      parseVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '{}');
        } catch (error) {
          console.error('[custom-product-options] Failed to parse variant data', error);
          return {};
        }
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;
        this.syncToVariant(event.data.variant?.id);
      }

      syncToVariant(variantId) {
        const info = this.variantData[variantId];
        this.currentRepeatCount = info ? info.repeatCount : 0;
        this.currentBaseKey = info ? info.baseKey : null;
        this.updateCards(this.currentRepeatCount);
      }

      updateCards(repeatCount) {
        this.querySelectorAll('[data-repeat-card]').forEach((card) => {
          const show = parseInt(card.dataset.repeatIndex, 10) <= repeatCount;
          card.style.display = show ? '' : 'none';
          if (!show) {
            card.querySelectorAll('input, select').forEach((input) => {
              if (input.type === 'radio' || input.type === 'checkbox') input.checked = false;
              else input.value = '';
            });
          }
        });
      }

      onGroupChange(event) {
        if (!event.target.classList.contains('custom-product-options__input')) return;
        this.reconcileUpgrades();
      }

      /**
       * Sum surcharges for global groups plus the per-unit groups on units 1..maxUnit.
       * Reads selected values directly, so it does not depend on current card visibility
       * (a not-yet-selected keycap contributes 0).
       */
      computeSurcharge(maxUnit) {
        let total = 0;
        this.querySelectorAll('.custom-product-options__group').forEach((group) => {
          if (group.dataset.repeats === 'true' && parseInt(group.dataset.repeatIndex, 10) > maxUnit) {
            return;
          }
          const checked = group.querySelector('input:checked') || group.querySelector('select');
          if (!checked) return;
          const source = checked.tagName === 'SELECT' ? checked.selectedOptions[0] : checked;
          const surcharge = parseFloat(source?.dataset.surcharge || '0');
          if (!Number.isNaN(surcharge)) total += surcharge;
        });
        return Math.round(total * 100) / 100;
      }

      repeatCountForBaseKey(baseKey) {
        const match = Object.values(this.variantData).find((v) => v.baseKey === baseKey);
        return match ? match.repeatCount : 0;
      }

      findVariantFor(baseKey, targetSurcharge) {
        const candidates = Object.values(this.variantData).filter((v) => v.baseKey === baseKey);
        if (!candidates.length) return null;

        const exact = candidates.find((v) => Math.abs(v.surcharge - targetSurcharge) < 0.005);
        if (exact) return exact;

        // No exact tier (shouldn't happen once the sync has run) — fall back to the closest tier
        // at or below the target rather than leaving the picker on an invalid combination.
        const atOrBelow = candidates
          .filter((v) => v.surcharge <= targetSurcharge)
          .sort((a, b) => b.surcharge - a.surcharge);
        return atOrBelow[0] || candidates.sort((a, b) => a.surcharge - b.surcharge)[0];
      }

      // Triggered by our own option inputs changing (base option unchanged): dispatch a real
      // change on the Upgrades input so Dawn fetches and re-renders price/media.
      reconcileUpgrades() {
        if (!this.hasUpgradesOption || !this.currentBaseKey) return;
        const match = this.findVariantFor(this.currentBaseKey, this.computeSurcharge(this.currentRepeatCount));
        if (!match) return;
        this.setUpgradeValue(match.upgradesValue, true);
      }

      // Triggered (capture phase) by a base option changing, e.g. "Number of Clickers": correct
      // the Upgrades value in the same tick, before VariantSelects collects selected options, so
      // only one fetch happens and it targets a valid, correctly-priced variant for the new unit
      // count. No dispatch here — Dawn's own upcoming fetch reads the corrected DOM state.
      onBaseOptionChangeCapture(event) {
        if (!this.hasUpgradesOption) return;
        const target = event.target;
        const optionName = target.dataset?.optionName;
        if (!optionName || optionName === 'Upgrades') return;
        if (!this.baseOptionNames.includes(optionName)) return;
        const vs = this.variantSelects;
        if (!vs || !vs.contains(target)) return;

        const newBaseKey = this.readCurrentBaseKeyFromDom();
        const newRepeatCount = this.repeatCountForBaseKey(newBaseKey);
        const match = this.findVariantFor(newBaseKey, this.computeSurcharge(newRepeatCount));
        if (!match) return;
        this.setUpgradeValue(match.upgradesValue, false);
      }

      readCurrentBaseKeyFromDom() {
        const vs = this.variantSelects;
        if (!vs) return null;
        const parts = this.baseOptionNames.map((name) => {
          const nodes = vs.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`);
          for (const node of nodes) {
            if (node.tagName === 'OPTION') {
              if (node.selected) return node.value;
            } else if (node.checked) {
              return node.value;
            }
          }
          return '';
        });
        return `${parts.join('||')}||`;
      }

      /**
       * Point the hidden Upgrades option at `value`. When dispatch is true, fire a change event
       * so Dawn fetches immediately (used when a base option didn't change). When false, just set
       * DOM state so an in-flight base-option change picks it up.
       */
      setUpgradeValue(value, dispatch) {
        const vs = this.variantSelects;
        if (!vs) return;
        const nodes = vs.querySelectorAll('[data-option-name="Upgrades"]');
        let node = null;
        for (const candidate of nodes) {
          if (candidate.value === value) {
            node = candidate;
            break;
          }
        }
        if (!node) return;

        if (node.tagName === 'OPTION') {
          const select = node.closest('select');
          if (!select || (select.value === value && !dispatch)) {
            if (select) select.value = value;
            return;
          }
          if (select.value === value) return;
          select.value = value;
          if (dispatch) select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Radio pill: check this one, clear siblings.
        if (node.checked && !dispatch) return;
        nodes.forEach((n) => {
          if (n !== node) n.checked = false;
        });
        if (node.checked) return;
        node.checked = true;
        if (dispatch) node.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSubmit(event) {
        const missing = this.validate();
        if (!missing.length) {
          this.clearError();
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(missing[0]);
      }

      validate() {
        const missing = [];
        this.querySelectorAll('.custom-product-options__group').forEach((group) => {
          if (group.dataset.required !== 'true') return;
          // Skip groups on hidden cards (units beyond the current repeat_count).
          if (group.dataset.repeats === 'true' && parseInt(group.dataset.repeatIndex, 10) > this.currentRepeatCount) {
            return;
          }
          const textInput = group.querySelector('input[type="text"]');
          const satisfied = textInput
            ? textInput.value.trim() !== ''
            : !!group.querySelector('input:checked') || !!group.querySelector('select')?.value;

          if (!satisfied) missing.push(group.dataset.label);
        });
        return missing;
      }

      showError(label) {
        let el = this.querySelector('.custom-product-options__error');
        if (!el) {
          el = document.createElement('p');
          el.className = 'custom-product-options__error form__message';
          this.prepend(el);
        }
        el.textContent = `Please select a ${label} before adding to cart.`;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      clearError() {
        this.querySelector('.custom-product-options__error')?.remove();
      }
    }
  );
}
